import { useEffect, useState } from 'react';
import { CircularProgress, Skeleton } from '@mui/material';
import { Checkbox, message } from 'antd';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputBase from '@mui/material/InputBase';
import styled from 'styled-components';
import { styled as MuiStyled } from '@mui/material/styles';
import { Formik } from 'formik';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment-timezone';

import useTranslation from 'customHooks/translate';
import { getRemainingDebt, paymentReceive } from 'services/invoices';
import { getPaymentMethods, getBankDatas } from 'services/others';
import { toastError } from 'services/helpers/getError';
import { getAccountData } from 'services/accounts';
import {
  ACCOUNT_INFO,
  BANK_DATAS,
  COST_DETAILS,
  GET_COST_PAYMENT_DETAILS,
  GET_INVOICE_PAYMENT_DETAILS,
  INVOICE_DETAILS,
  PAYMENT_METHODS,
} from 'events/types';

import CustomDialog from './CustomDialog';

const BootstrapInput = MuiStyled(InputBase)(({ theme }) => ({
  '& .MuiInputBase-input': {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #ced4da',
    fontSize: 16,
    padding: '10px 26px 10px 12px',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    '&:focus': {
      borderRadius: 4,
      borderColor: '#80bdff',
      boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
    },
  },
}));

const PaymentModal = (props) => {
  const invoiceId = props.invoiceDetails?.id;
  const queryClient = useQueryClient();
  const translation = useTranslation();
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>(
    {}
  );
  const [isRemainingDebtLoading, setIsRemainingDebtLoading] = useState(false);
  const [isPaymentSubmitting, setIsPaymentSubmitting] = useState(false);

  const [paymentAmount, setPaymentAmount] = useState<string | null>(null);
  const [taxAmount, setTaxAmount] = useState<string | null>(null);
  const [fullPayment, setFullPayment] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const { data: paymentMethods, refetch: refetchPaymentMethods } = useQuery({
    queryKey: [PAYMENT_METHODS],
    queryFn: () => getPaymentMethods(),
    select: (response) => response.data.data,
  });

  const { data: bankDatas, refetch: refetchBankDatas } = useQuery({
    queryKey: [BANK_DATAS],
    queryFn: () => getBankDatas(),
    select: (response) => response.data.data,
  });

  const accountInfoQuery = useQuery({
    queryKey: [ACCOUNT_INFO],
    queryFn: getAccountData,
    select: (response) => response.data.data,
  });

  useEffect(() => {
    if (props.open) {
      refetchPaymentMethods();
      refetchBankDatas();
    }
  }, [props.open, refetchPaymentMethods, refetchBankDatas]);

  const handleFullPaymentChange = (e) => {
    const isChecked = e.target.checked;
    setFullPayment(isChecked);

    if (isChecked) {
      setIsRemainingDebtLoading(true);
      setIsReadOnly(true);

      getRemainingDebt(invoiceId)
        .then((data) => {
          setPaymentAmount(data.data.data.mainBalance.toString());
          setTaxAmount(data.data.data.taxBalance.toString());
          setIsRemainingDebtLoading(false);
        })
        .catch((e) => {
          toastError(e);
          setIsRemainingDebtLoading(false);
          setFullPayment(false);
          setIsReadOnly(false);
        });
    } else {
      setPaymentAmount(null);
      setTaxAmount(null);
      setIsReadOnly(false);
    }
  };

  const handleSubmit = (values) => {
    setIsPaymentSubmitting(true);

    const payload = {
      ...values,
      paymentAmount: paymentAmount ? +paymentAmount : 0,
      taxAmount: taxAmount ? +taxAmount : 0,
      fullPayment: fullPayment,
      invoiceId: invoiceId,
    };

    paymentReceive(payload)
      .then(() => {
        props.handleClose();
        message.success(`${translation.messages.successAdded}`);
        queryClient.invalidateQueries([GET_INVOICE_PAYMENT_DETAILS, props.id]);
        queryClient.invalidateQueries([GET_COST_PAYMENT_DETAILS, props.id]);
        queryClient.invalidateQueries([INVOICE_DETAILS, props.id]);
        queryClient.invalidateQueries([COST_DETAILS, props.id]);

        if (props.onPaymentAdded) {
          props.onPaymentAdded();
        }
      })
      .catch((err) => {
        const errorResponse = err.response?.data?.errors || '';
        err.response?.data?.errors[0] &&
          message.error(err.response?.data?.errors[0].message);
        const getErrorMessage = (errorArray) => {
          if (!errorArray || !Array.isArray(errorArray)) return undefined;

          const validMessages = errorArray.filter(
            (msg) => msg && msg.trim() !== ''
          );
          return validMessages.length > 0
            ? validMessages.join(', ')
            : undefined;
        };

        setErrors({
          PaymentAmount: getErrorMessage(errorResponse.PaymentAmount),
          PaymentMethodID: getErrorMessage(errorResponse.PaymentMethodID),
          BankId: errorResponse.BankId?.[0],
          Description: errorResponse.Description?.[0],
        });
      })
      .finally(() => {
        setIsPaymentSubmitting(false);
      });
  };

  const [initialValues, setInitialValues] = useState({
    paymentMethodID: 0,
    isSendNotificationEmail: false,
    notificationEmail: '',
    bankId: 0,
    description: '',
    localpaymentDate: '',
  });

  useEffect(() => {
    if (accountInfoQuery.data?.timeZoneId) {
      const today = moment()
        .tz(accountInfoQuery.data.timeZoneId)
        .format('YYYY-MM-DD');
      setInitialValues((prevValues) => ({
        ...prevValues,
        localpaymentDate: today,
      }));
    }
  }, [accountInfoQuery.data?.timeZoneId]);

  return (
    <CustomDialog
      open={props.open}
      handleClose={props.handleClose}
      buttonTitle={translation.PaymentReceived.Save}
      isLoading={isPaymentSubmitting || isRemainingDebtLoading}
      title={props.title}
      onSubmit={handleSubmit}
      hasOwnButton
    >
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, setFieldValue, handleSubmit }) => (
          <div className="flex flex-col gap-[20px]">
            <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[10px]">
              <div className="flex flex-col gap-[12px]">
                <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                  {props.isCost
                    ? translation.PaymentReceived.ReceivedPaymentCost
                    : translation.PaymentReceived.ReceivedPayment}
                </label>
                <Input
                  type="date"
                  name="localpaymentDate"
                  value={values.localpaymentDate}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col gap-[12px]">
                <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                  {translation.PaymentReceived.PaymentMethod}
                </label>
                <Select
                  className="select-input"
                  fullWidth
                  value={values.paymentMethodID}
                  onChange={handleChange}
                  name="paymentMethodID"
                  inputProps={{ 'aria-label': 'Without label' }}
                  style={{
                    borderColor:
                      errors.PaymentMethodID && !values.paymentMethodID
                        ? 'red'
                        : '',
                  }}
                >
                  <MenuItem value={0} disabled>
                    {translation.PaymentReceived.choose}..
                  </MenuItem>
                  {paymentMethods?.map((item) => (
                    <MenuItem value={item.id} key={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.PaymentMethodID && !values.paymentMethodID && (
                  <span className="text-[12px] text-red-600">
                    {errors.PaymentMethodID}
                  </span>
                )}
              </div>
            </div>

            {values.paymentMethodID === 1 && (
              <div className="flex flex-col gap-[12px]">
                <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                  {translation.bankAdd.Name}
                </label>
                <Select
                  value={values.bankId}
                  onChange={handleChange}
                  name="bankId"
                  inputProps={{ 'aria-label': 'Without label' }}
                  style={{
                    borderColor: errors.BankId && !values.bankId ? 'red' : '',
                  }}
                >
                  <MenuItem value={0} disabled>
                    {translation.PaymentReceived.choose}..
                  </MenuItem>
                  {bankDatas?.map((item) => (
                    <MenuItem value={item.id} key={item.id}>
                      {item.bankName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.BankId && !values.bankId && (
                  <span className="text-[12px] text-red-600">
                    {errors.BankId}
                  </span>
                )}
              </div>
            )}

            {values.paymentMethodID === 4 && (
              <div className="flex flex-col gap-[12px]">
                <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                  {translation.PaymentReceived.comment}
                </label>
                <BootstrapInput
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  style={{
                    borderColor:
                      errors.Description && !values.description
                        ? 'red'
                        : '#ccc',
                  }}
                />
                {errors.Description && !values.description && (
                  <span className="text-[12px] text-red-600">
                    {errors.Description}
                  </span>
                )}
              </div>
            )}

            <div className="flex flex-col gap-[12px]">
              <p className="text-[16px] font-semibold text-[#1D1D1D] md:text-[20px]">
                {props.isCost
                  ? translation.PaymentReceived.ReceivedDebtsPaymentCost
                  : translation.PaymentReceived.ReceivedDebtsPayment}
              </p>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] items-end gap-[10px]">
                <div className="flex flex-col gap-[12px]">
                  <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                    {props.isCost
                      ? translation.PaymentReceived.PaidAmount
                      : translation.PaymentReceived.ReceivedAmount}
                  </label>
                  <div>
                    {isRemainingDebtLoading ? (
                      <Skeleton variant="rectangular" width={246} height={48} />
                    ) : (
                      <BootstrapInput
                        value={paymentAmount === null ? '' : paymentAmount}
                        onChange={(e) =>
                          !isReadOnly && setPaymentAmount(e.target.value)
                        }
                        onFocus={(e) => {
                          if (e.target.value === '0') {
                            setPaymentAmount('');
                          }
                        }}
                        onBlur={(e) => {
                          if (e.target.value === '') {
                            setPaymentAmount(null);
                          }
                        }}
                        readOnly={isReadOnly}
                        style={{
                          borderColor:
                            errors.PaymentAmount && !paymentAmount
                              ? 'red'
                              : '#ccc',
                        }}
                      />
                    )}
                  </div>
                  {errors.PaymentAmount && (
                    <span className="text-[12px] text-red-600">
                      {errors.PaymentAmount}
                    </span>
                  )}
                </div>
                <Checkbox
                  name="fullPayment"
                  onChange={handleFullPaymentChange}
                  checked={fullPayment}
                >
                  %100 {translation.PaymentReceived.payment}
                </Checkbox>
              </div>
            </div>

            <div className="flex flex-col gap-[12px]">
              <p className="text-[16px] font-semibold text-[#1D1D1D] md:text-[20px]">
                {props.isCost
                  ? translation.PaymentReceived.PaidPaymentonTax
                  : translation.PaymentReceived.ReceivedPaymentonTax}
              </p>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[10px]">
                <div className="flex flex-col gap-[12px]">
                  <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                    {props.isCost
                      ? translation.PaymentReceived.PaidAmount
                      : translation.PaymentReceived.ReceivedAmount}
                  </label>
                  <div>
                    {isRemainingDebtLoading ? (
                      <Skeleton variant="rectangular" width={246} height={48} />
                    ) : (
                      <BootstrapInput
                        value={taxAmount === null ? '' : taxAmount}
                        onChange={(e) =>
                          !isReadOnly && setTaxAmount(e.target.value)
                        }
                        onFocus={(e) => {
                          if (e.target.value === '0') {
                            setTaxAmount('');
                          }
                        }}
                        onBlur={(e) => {
                          if (e.target.value === '') {
                            setTaxAmount(null);
                          }
                        }}
                        readOnly={isReadOnly}
                      />
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-[12px]">
                  <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                    {translation.creditorAdd.Email}
                  </label>
                  <BootstrapInput
                    name="notificationEmail"
                    value={values.notificationEmail}
                    onChange={handleChange}
                    readOnly={
                      values.isSendNotificationEmail &&
                      values.notificationEmail !== ''
                    }
                  />
                </div>
              </div>
              <Checkbox
                name="isSendNotificationEmail"
                checked={values.isSendNotificationEmail}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setFieldValue('isSendNotificationEmail', isChecked);

                  if (isChecked) {
                    setFieldValue(
                      'notificationEmail',
                      props.invoiceDetails?.partnerEmail
                    );
                  } else {
                    setFieldValue('notificationEmail', '');
                  }
                }}
              >
                {translation.PaymentReceived.SendNotification}
              </Checkbox>
            </div>
            <button
              onClick={() => handleSubmit()}
              type="submit"
              className="w-full rounded-[8px] bg-[#6dcd90] px-[24px] py-[12px] text-[14px] font-normal leading-[16px] text-white md:text-[16px]"
            >
              {isPaymentSubmitting || isRemainingDebtLoading ? (
                <CircularProgress size={22} />
              ) : (
                translation.PaymentReceived.Save
              )}
            </button>
          </div>
        )}
      </Formik>
    </CustomDialog>
  );
};

export default PaymentModal;

const Input = styled.input`
  width: 100%;
  height: 56px;
  border-radius: 6px;
  padding: 12.7px 15px;
  border: 1px solid rgba(41, 45, 50, 0.25);
  :focus {
    outline: none !important;
    border: 2px solid #466afb;
    padding: 11.9px 14px;
  }
`;
